import { apiGet, apiPut, apiPost, apiDelete } from './client';

export type IntegrationProvider =
  | 'mapbox'
  | 'mls_reso'
  | 'mls_idx'
  | 'email_smtp'
  | 'email_sendgrid'
  | 'email_mailgun'
  | 'email_postmark'
  // The platform's own email has run on Resend since the comms service was written; an agent
  // bringing their own key had nowhere to put it until migration 069 widened the enum.
  | 'email_resend'
  | 'sms_twilio'
  | 'slack'
  | 'google_analytics'
  | 'facebook_pixel';

export interface SiteIntegration {
  id: string;
  tenant_id: string;
  integration_type: IntegrationProvider;
  is_enabled: boolean;
  is_configured: boolean;
  last_tested_at: string | null;
  last_test_result: 'success' | 'failed' | null;
  last_error: string | null;
  settings: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface TestResult {
  success: boolean;
  message: string;
  details?: unknown;
}

export async function listSiteIntegrations(): Promise<SiteIntegration[]> {
  const res = await apiGet<{ integrations: SiteIntegration[] }>('/site-integrations');
  return res.integrations;
}

export async function getSiteIntegration(type: IntegrationProvider): Promise<SiteIntegration | null> {
  const res = await apiGet<{ integration: SiteIntegration | null }>(`/site-integrations/${type}`);
  return res.integration;
}

/**
 * Saving MLS credentials returns whether they were REGISTERED with the listing engine, not just
 * stored. The two came apart badly: a customer's keys sat encrypted in a row nothing read while
 * every screen showed a saved state, and his site served demo listings for days.
 */
export interface MlsConnectOutcome {
  connected: boolean;
  message: string;
}

export async function upsertSiteIntegration(
  type: IntegrationProvider,
  data: {
    credentials?: Record<string, unknown>;
    settings?: Record<string, unknown>;
    is_enabled?: boolean;
  },
): Promise<SiteIntegration & { mls?: MlsConnectOutcome }> {
  const res = await apiPut<{ integration: SiteIntegration; mls?: MlsConnectOutcome }>(
    `/site-integrations/${type}`,
    data,
  );
  return { ...res.integration, ...(res.mls ? { mls: res.mls } : {}) };
}

export async function deleteSiteIntegration(type: IntegrationProvider): Promise<void> {
  await apiDelete(`/site-integrations/${type}`);
}

export async function testSiteIntegration(
  type: IntegrationProvider,
  credentials?: Record<string, unknown>,
): Promise<TestResult> {
  return apiPost<TestResult>(`/site-integrations/${type}/test`, credentials ?? {});
}
